import { useCallback, useEffect, useRef, useState } from 'react';
import { BehaviorSubject, from, of, Subject } from 'rxjs';
import { catchError, map, skip, switchMap, tap } from 'rxjs/operators';
import { ConfigurationApi } from 'api/timesheets/configuration/api';
import { Configuration } from 'api/timesheets/configuration/configuration';
import { Result } from 'core/result';
import { Empty, Loaded, Loading, revert, Updating } from 'core/state';
import { queryClient } from 'queries';
// Importing from index file was causing some breaking changes, hence imported directly from the hook file
import { TIMESHEET_CONFIGURATION_KEY } from 'queries/timesheets/configuration';
import { logger } from 'services';
import { SetState, State, UseStateResult } from './state';

const NO_OP = () => {};

export const useConfiguration = (): UseStateResult<Configuration> => {
  const configuration$ = useRef(
    new BehaviorSubject<State<Configuration>>(Empty()),
  );
  const next$ = useRef(new Subject<Configuration>());
  const setConfiguration = useCallback<SetState<Configuration>>(
    (next: Configuration) => next$.current.next(next),
    [next$],
  );
  const [configuration, setConfigurationInternal] = useState<
    State<Configuration>
  >(Empty());
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    const subscription = configuration$.current
      .asObservable()
      .pipe(skip(1))
      .subscribe(setConfigurationInternal);

    return () => subscription.unsubscribe();
  }, [configuration$, setConfigurationInternal]);

  useEffect(() => {
    if (initialized) {
      return NO_OP;
    }

    configuration$.current.next(Loading());
    const subscription = from(ConfigurationApi.get()).subscribe(
      (config) => configuration$.current.next(Loaded(config)),
      (error: Error) => {
        logger.error('failed to fetch timesheets configuration', { error });
        configuration$.current.next(Empty());
      },
      () => setInitialized(true),
    );

    return () => subscription.unsubscribe();
  }, [initialized, configuration$, setInitialized]);

  useEffect(() => {
    if (!initialized) {
      return NO_OP;
    }
    const subscription = next$.current
      .pipe(
        tap((next) => {
          const previous = configuration$.current.value[1];

          configuration$.current.next(
            previous == null ? Loading() : Updating(next, previous),
          );
        }),
        switchMap((next) =>
          from(ConfigurationApi.put(next)).pipe(
            map((x) => {
              queryClient.setQueryData(TIMESHEET_CONFIGURATION_KEY, x);

              return x;
            }),
            map(Result.ok),
            catchError((error: Error) => of(Result.error(error))),
          ),
        ),
      )
      .subscribe(
        Result.on<Configuration, Error>(
          (x) => configuration$.current.next(Loaded(x)),
          (error: Error) => {
            const config = configuration$.current.value;

            logger.error('failed to update timesheets configuration', {
              error,
              configuration: config,
            });
            if (config[0] === 'updating') {
              configuration$.current.next(revert(config));
            }
          },
        ),
      );

    return () => subscription.unsubscribe();
  }, [initialized, configuration$, next$]);

  return [configuration, setConfiguration];
};
